<template>
  <draggable
    :force-fallback="true"
    :scroll-sensitivity="150"
    drag-class="table-drag"
  >
    <table
      ref="HtmlTableForm"
      @mouseleave="setHoverIndex(-1, -1)"
      :style="{ width: scrollable ? 'auto' : '100%' }"
    >
      <draggable
        :list="tableContent"
        handle=".row-handle"
        :delay="50"
        ghost-class="ghost"
        drag-class="drag"
        :force-fallback="true"
        :scroll-sensitivity="150"
        tag="tbody"
        :options="{ group: 'row' }"
        @choose.stop="({ oldIndex }) => chooseRowFrom(oldIndex)"
        @end="sortRow"
      >
        <template v-for="(row, rowIndex) in tableContent">
          <draggable
            v-if="rowIndex === 0"
            :list="tableHead"
            ghost-class="ghost"
            handle=".column-handle"
            :delay="50"
            :force-fallback="true"
            :scroll-sensitivity="150"
            tag="tr"
            :key="rowIndex"
            :options="{ group: 'column' }"
            @choose.stop="({ oldIndex }) => chooseColumnFrom(oldIndex)"
            @end="sortColumn"
          >
            <Cell
              v-for="(cell, cellIndex) in row"
              :ref="`tableHeadCells_${cellIndex}`"
              :key="`${rowIndex}_${cellIndex}`"
              :cell="cell"
              :chosen="chosenCellIndexIs(rowIndex, cellIndex)"
              :editing="isEditingCell(rowIndex, cellIndex)"
              :sticky="isStickyColumn(cellIndex)"
              :before-columns-width="sumBeforeCellWidths(cellIndex)"
              @hover="setHoverIndex(rowIndex, cellIndex)"
              @focus="onfocusEvent(rowIndex, cellIndex)"
              @editor-blur="setEditingIndex(-1, -1)"
              @mousedown="selectRangeStart(rowIndex, cellIndex)"
              @mouseDrag="selectRangeOver(rowIndex, cellIndex)"
              @mouseup="selectRangeEnd(rowIndex, cellIndex)"
              @dblclick="setEditingIndex(rowIndex, cellIndex)"
              @keydown="($event) => controlKeyDown($event, rowIndex, cellIndex)"
              @change="(value) => setContent(rowIndex, cellIndex, value)"
            >
              <template v-slot:row-bulk-select>
                <RowBulkSelectButton
                  v-if="!isDrag && cellIndex === 0 && rowIndex === hoverIndex.y"
                  :key="`row_handler_${rowIndex}`"
                  @click="chooseRowFrom(rowIndex)"
                />
              </template>
              <template v-slot:column-bulk-select>
                <div
                  class="d-flex"
                  style="justify-content: center; width: 100%"
                >
                  <ColumnBulkSelectButton
                    v-if="
                      !isDrag && rowIndex === 0 && cellIndex === hoverIndex.x
                    "
                    :key="`column_handler_${cellIndex}`"
                    @click="chooseColumnFrom(cellIndex)"
                  />
                </div>
              </template>
            </Cell>
          </draggable>
          <tr v-else :key="rowIndex">
            <Cell
              v-for="(cell, cellIndex) in row"
              :ref="`tableHeadCells_${cellIndex}`"
              :key="`${rowIndex}_${cellIndex}`"
              :cell="cell"
              :chosen="chosenCellIndexIs(rowIndex, cellIndex)"
              :editing="isEditingCell(rowIndex, cellIndex)"
              :sticky="isStickyColumn(cellIndex)"
              :before-columns-width="sumBeforeCellWidths(cellIndex)"
              @hover="setHoverIndex(rowIndex, cellIndex)"
              @focus="onfocusEvent(rowIndex, cellIndex)"
              @editor-blur="setEditingIndex(-1, -1)"
              @mousedown="selectRangeStart(rowIndex, cellIndex)"
              @mouseDrag="selectRangeOver(rowIndex, cellIndex)"
              @mouseup="selectRangeEnd(rowIndex, cellIndex)"
              @dblclick="setEditingIndex(rowIndex, cellIndex)"
              @keydown="($event) => controlKeyDown($event, rowIndex, cellIndex)"
              @change="(value) => setContent(rowIndex, cellIndex, value)"
            >
              <template v-slot:row-bulk-select>
                <RowBulkSelectButton
                  v-if="!isDrag && cellIndex === 0 && rowIndex === hoverIndex.y"
                  :key="`row_handler_${rowIndex}`"
                  @click="chooseRowFrom(rowIndex)"
                />
              </template>
            </Cell>
          </tr>
        </template>
      </draggable>
    </table>
  </draggable>
</template>

<script>
import draggable from 'vuedraggable'
import itemUtilsMixin from '@/mixins/item_utils'
import ColumnBulkSelectButton from './ColumnBulkSelectButton'
import RowBulkSelectButton from './RowBulkSelectButton'
import Cell from './Cell'

export default {
  name: 'TableForm',
  components: {
    Cell,
    RowBulkSelectButton,
    ColumnBulkSelectButton,
    draggable
  },
  mixins: [itemUtilsMixin],
  props: {
    targetRangeStartIndex: { type: Object, required: true },
    targetRangeEndIndex: { type: Object, required: true }
  },
  computed: {
    scrollable: {
      get () {
        return this.editingItemable.scrollable
      },
      set (value) {
        this.updateEditingItemable(value, 'scrollable')
      }
    },
    tableContent: {
      get () {
        return [...this.editingItemable.htmlTableContent]
      },
      set (value) {
        this.updateEditingItemable(value, 'htmlTableContent')
      }
    },
    tableHead () {
      return [...(this.tableContent[0] || [])]
    },
    stickyColumnIndex () {
      return this.editingItemable.tableColumnFixed
    },
    isEditing () {
      return this.editingIndex.x !== -1 && this.editingIndex.y !== -1
    }
  },
  data () {
    return {
      loaded: true,
      isDrag: false,
      hoverIndex: { y: -1, x: -1 },
      editingIndex: { y: -1, x: -1 }
    }
  },
  mounted () {
    document.addEventListener('mouseup', this.selectRangeDragReset, true)
  },
  destroyed () {
    document.removeEventListener('mouseup', this.selectRangeDragReset, true)
  },
  methods: {
    controlKeyDown (event, y, x) {
      if (this.isEditing) return
      switch (event.code) {
        case 'ArrowUp':
          this.sendSelectCellIndexes(
            this.targetRangeStartIndex.y - 1,
            this.targetRangeStartIndex.x,
            this.targetRangeStartIndex.y - 1,
            this.targetRangeStartIndex.x
          )
          break
        case 'ArrowRight':
          this.sendSelectCellIndexes(
            this.targetRangeStartIndex.y,
            this.targetRangeStartIndex.x + 1,
            this.targetRangeStartIndex.y,
            this.targetRangeStartIndex.x + 1
          )
          break
        case 'ArrowDown':
          this.sendSelectCellIndexes(
            this.targetRangeStartIndex.y + 1,
            this.targetRangeStartIndex.x,
            this.targetRangeStartIndex.y + 1,
            this.targetRangeStartIndex.x
          )
          break
        case 'ArrowLeft':
          this.sendSelectCellIndexes(
            this.targetRangeStartIndex.y,
            this.targetRangeStartIndex.x - 1,
            this.targetRangeStartIndex.y,
            this.targetRangeStartIndex.x - 1
          )
          break
        default:
          this.setEditingIndex(y, x)
      }
    },
    onfocusEvent (y, x) {
      if (
        !this.isDrag &&
        this.targetRangeStartIndex.y !== -1 &&
        this.targetRangeStartIndex.x !== -1
      ) {
        this.chooseRangeStartCellFrom(y, x)
      }
    },
    isStickyColumn (index) {
      return this.scrollable && this.stickyColumnIndex >= index
    },
    sumBeforeCellWidths (index) {
      return this.tableHead
        .map((_, cellIndex) =>
          this.$refs[`tableHeadCells_${cellIndex}`] &&
          this.$refs[`tableHeadCells_${cellIndex}`][0]
            ? this.$refs[`tableHeadCells_${cellIndex}`][0].$el.offsetWidth
            : 0
        )
        .filter((_, cellIndex) => index > cellIndex)
        .reduce((prev, current) => {
          return prev + current
        }, 0)
    },
    chooseColumnFrom (index) {
      this.sendSelectCellIndexes(-1, index, -1, -1)
    },
    chooseRowFrom (index) {
      this.sendSelectCellIndexes(index, -1, -1, -1)
    },
    chosenCellIndexIs (y, x) {
      if (
        this.targetRangeEndIndex.x !== -1 &&
        this.targetRangeEndIndex.y !== -1 &&
        this.targetRangeStartIndex.x !== -1 &&
        this.targetRangeStartIndex.y !== -1
      ) {
        return (
          Math.min(this.targetRangeEndIndex.y, this.targetRangeStartIndex.y) <=
            y &&
          y <=
            Math.max(
              this.targetRangeEndIndex.y,
              this.targetRangeStartIndex.y
            ) &&
          Math.min(this.targetRangeEndIndex.x, this.targetRangeStartIndex.x) <=
            x &&
          x <=
            Math.max(this.targetRangeEndIndex.x, this.targetRangeStartIndex.x)
        )
      }
      if (this.isEditingCell(y, x)) {
        return true
      }
      if (this.targetRangeStartIndex.y === -1) {
        return this.targetRangeStartIndex.x === x
      }
      if (this.targetRangeStartIndex.x === -1) {
        return this.targetRangeStartIndex.y === y
      }
      return false
    },
    isEditingCell (y, x) {
      return this.editingIndex.y === y && this.editingIndex.x === x
    },
    chooseRangeStartCellFrom (y, x) {
      this.sendSelectCellIndexes(y, x, y, x)
    },
    chooseRangeEndCellFrom (y, x) {
      this.sendSelectCellIndexes(
        this.targetRangeStartIndex.y,
        this.targetRangeStartIndex.x,
        y,
        x
      )
    },
    setEditingIndex (y, x) {
      this.editingIndex.x = x
      this.editingIndex.y = y
    },
    chosenColumn () {
      return (
        this.targetRangeStartIndex.x !== -1 &&
        this.targetRangeStartIndex.y === -1
      )
    },
    chosenRow () {
      return (
        this.targetRangeStartIndex.y !== -1 &&
        this.targetRangeStartIndex.x === -1
      )
    },
    chosenCells () {
      return (
        this.targetRangeStartIndex.y !== -1 ||
        this.targetRangeStartIndex.x !== -1
      )
    },
    sortRow ({ newIndex, oldIndex }) {
      const copyContent = this.editingItemable.htmlTableContent.map((row) => [
        ...row
      ])
      const rowData = copyContent[oldIndex]
      copyContent[oldIndex] = undefined
      newIndex > oldIndex
        ? copyContent.splice(newIndex + 1, 0, rowData)
        : copyContent.splice(newIndex, 0, rowData)
      this.tableContent = copyContent.filter((row) => !!row)
      this.chooseRowFrom(newIndex)
    },
    sortColumn ({ newIndex, oldIndex }) {
      const copyContent = this.editingItemable.htmlTableContent.map((row) => [
        ...row
      ])
      this.tableContent = copyContent.map((row) => {
        const columnData = row[oldIndex]
        const copyRow = [...row.map((cell) => ({ ...cell }))]
        copyRow[oldIndex] = undefined
        newIndex > oldIndex
          ? copyRow.splice(newIndex + 1, 0, columnData)
          : copyRow.splice(newIndex, 0, columnData)
        return copyRow.filter((val) => !!val)
      })
      this.chooseColumnFrom(newIndex)
    },
    setHoverIndex (y, x) {
      this.hoverIndex = { y, x }
    },
    setContent (y, x, content) {
      const copyTable = this.duplicateTableContent()
      copyTable[y][x].content = content
      this.tableContent = copyTable
    },
    duplicateTableContent () {
      return [
        ...this.tableContent.map((row) => [
          ...row.map((cell) => ({ ...cell }))
        ])
      ]
    },
    selectRangeStart (y, x) {
      if (this.isEditingCell(y, x)) return

      this.isDrag = true
      this.sendSelectCellIndexes(y, x, -1, -1)
      this.setEditingIndex(-1, -1)
    },
    selectRangeOver (y, x) {
      if (!this.isDrag || this.isEditing) return

      this.chooseRangeEndCellFrom(y, x)
    },
    selectRangeDragReset (e) {
      if (
        this.$refs.HtmlTableForm &&
        !this.$refs.HtmlTableForm.contains(e.target)
      ) {
        this.isDrag = false
      }
    },
    selectRangeEnd (y, x) {
      if (!this.isDrag || this.isEditing) return

      this.chooseRangeEndCellFrom(y, x)
      this.isDrag = false
    },
    sendSelectCellIndexes (startY, startX, endY, endX) {
      this.$emit('select-cell-index', {
        start: { y: startY, x: startX },
        end: { y: endY, x: endX }
      })
    }
  }
}
</script>

<style scoped>
table,
th,
td {
  border: 1px solid #aaa;
}

table {
  overflow-x: auto;
  border-spacing: 0;
}

tbody {
  white-space: nowrap;
}

th,
td {
  padding: 1px;
  border-top-width: 0;
  border-left-width: 0;
}

th:last-child,
td:last-child {
  border-right-width: 0;
}

tr:last-child td {
  border-bottom-width: 0;
}

.ghost,
.chosen {
  opacity: 1;
  box-shadow: 0 0 0 2px rgba(49, 94, 217, 1) inset;
}

.drag {
  display: block;
  opacity: 0;
  width: inherit;
}

.table-drag {
  display: none;
}
</style>
